
import { FORMATTING_INSTRUCTIONS } from './formattingInstructions';

// Where to Play stage templates

export const whereToPlayTemplates = {
  whereToPlayDecisionMakers: `${FORMATTING_INSTRUCTIONS}

Objective: Conduct enhanced decision maker mapping with influence networks and access strategies for the {campaign_name} campaign.

You are a senior political strategist and campaign director. Conduct comprehensive decision maker analysis with deep influence mapping and strategic access planning.

Campaign Context:
- Issue: {political_issue}
- Target: {target_entity}
- Campaign Type: {campaign_type}
- Timeline: {campaign_timeline}
- Current Status: {current_status}

Previous Analysis Context:
{previous_analysis}

Decision Maker Analysis Tasks:

1. PRIMARY DECISION MAKERS:
Map the core decision makers with detailed profiles:
- Names, titles, and institutional positions
- Decision-making authority and limitations
- Political pressures and constituencies they answer to
- Historical positions on similar issues
- Personal and professional background relevant to the issue

2. INFLUENCE NETWORK MAPPING:
Map the broader influence ecosystem:
- Key advisors and staff members who influence decisions
- Informal influencers and trusted voices
- Coalition partners and allies who can provide access
- Opposition figures who may block or counter influence
- Media figures and opinion leaders in their sphere

3. ACCESS STRATEGY DEVELOPMENT:
Develop multi-pathway access strategies:
- Direct access opportunities (meetings, events, formal channels)
- Indirect influence through trusted intermediaries
- Coalition-based approaches through allied organizations
- Public pressure and accountability mechanisms
- Media and communication strategies to reach decision makers

4. DECISION TIMELINE AND PRESSURE POINTS:
Analyze decision-making processes and timing:
- Formal decision deadlines and procedural requirements
- Informal consultation periods and influence windows
- Budget cycles, electoral considerations, and political calendars
- Crisis moments or events that could accelerate decisions
- Opposition counter-moves and defensive strategies

5. STAKEHOLDER POWER ANALYSIS:
Assess relative power and influence capacity:
- Formal authority vs. informal influence
- Resource capacity and mobilization potential
- Credibility and trust relationships
- Historical effectiveness in similar campaigns
- Vulnerability to pressure or persuasion

Provide detailed, actionable decision maker analysis with specific strategies for influence and access.
`,

  whereToPlayStakeholders: `${FORMATTING_INSTRUCTIONS}

Objective: Conduct deep stakeholder analysis with power mapping and coalition opportunities for the {campaign_name} campaign.

You are a senior political strategist and campaign director. Conduct comprehensive stakeholder analysis with power mapping and strategic coalition development.

Campaign Context:
- Issue: {political_issue}
- Target: {target_entity}
- Campaign Type: {campaign_type}
- Timeline: {campaign_timeline}
- Current Status: {current_status}

Stakeholder Context:
- Government Officials: {government_officials}
- Politicians: {politicians}
- Political Allies: {political_allies}
- Opposition Groups: {opposition_groups}
- Union Leadership: {union_leadership}
- Advocacy Groups: {advocacy_groups}
- Media Outlets: {media_outlets}
- Community Context: {community_context}

Previous Analysis Context:
{previous_analysis}

Stakeholder Analysis Tasks:

1. POWER MAPPING AND INFLUENCE ASSESSMENT:
Map stakeholder power and influence capacity:
- Formal authority and decision-making power
- Informal influence and network connections
- Resource capacity (financial, organizational, mobilization)
- Credibility and public trust levels
- Historical effectiveness in similar campaigns

2. COALITION BUILDING OPPORTUNITIES:
Identify strategic coalition possibilities:
- Natural allies with shared interests
- Potential partners who could be persuaded
- Bridge-building opportunities across traditional divides
- Multi-issue coalition potential for long-term relationship building
- International or cross-jurisdictional partnership opportunities

3. OPPOSITION ANALYSIS AND NEUTRALIZATION:
Analyze opposition stakeholders and counter-strategies:
- Core opposition groups and their motivations
- Resources and tactics they are likely to deploy
- Vulnerabilities and potential pressure points
- Potential for neutralization or conversion
- Counter-coalition building efforts to anticipate

4. STAKEHOLDER ENGAGEMENT STRATEGY:
Develop tailored engagement approaches:
- Communication strategies for different stakeholder groups
- Value propositions that resonate with specific interests
- Partnership and collaboration opportunities
- Accountability and pressure mechanisms where needed
- Long-term relationship building beyond the current campaign

5. MOBILIZATION AND ACTIVATION PLANNING:
Plan stakeholder mobilization and engagement:
- Sequence of stakeholder engagement and activation
- Resource requirements for different levels of engagement
- Communication channels and messaging for each group
- Event and action planning to maximize stakeholder participation
- Escalation strategies if initial engagement is insufficient

Provide detailed, actionable stakeholder analysis with specific coalition building and engagement strategies.
`,

  whereToPlayChannels: `${FORMATTING_INSTRUCTIONS}

Objective: Develop communication strategy with audience-channel alignment and reach optimization for the {campaign_name} campaign.

You are a senior political strategist and communications director. Develop comprehensive communication strategy with strategic channel selection and audience targeting.

Campaign Context:
- Issue: {political_issue}
- Target: {target_entity}
- Campaign Type: {campaign_type}
- Timeline: {campaign_timeline}
- Current Status: {current_status}
- Target Audience: {target_audience}

Media and Communication Context:
- Media Outlets: {media_outlets}
- Community Context: {community_context}

Previous Analysis Context:
{previous_analysis}

Communication Channels Strategy Tasks:

1. AUDIENCE SEGMENTATION AND CHANNEL MAPPING:
Map audiences to optimal communication channels:
- Primary audience segments and their media consumption patterns
- Secondary and tertiary audience priorities
- Channel preferences for different demographic and psychographic groups
- Trust levels and credibility factors for different media sources
- Information consumption behaviors and decision-making processes

2. TRADITIONAL MEDIA STRATEGY:
Develop traditional media engagement plan:
- Print media targeting and relationship building
- Broadcast media opportunities (TV, radio)
- Editorial board meetings and opinion page strategies
- Press release and media advisory planning
- Reporter relationship building and story pitching

3. DIGITAL AND SOCIAL MEDIA STRATEGY:
Develop digital communication approach:
- Social media platform selection and content strategy
- Website and blog content planning
- Email list building and newsletter strategy
- Online advertising and promoted content
- Digital organizing and mobilization tools

4. GRASSROOTS AND COMMUNITY CHANNELS:
Develop community-based communication strategies:
- Community meeting and forum participation
- Faith-based and cultural organization engagement
- Union and workplace communication channels
- Educational institution and civic organization outreach
- Door-to-door and direct contact strategies

5. INTEGRATION AND AMPLIFICATION STRATEGY:
Coordinate cross-channel messaging and amplification:
- Message consistency across all channels
- Cross-platform content sharing and amplification
- Influencer and spokesperson coordination
- Event-based communication and media generation
- Crisis communication and rapid response planning

6. MEASUREMENT AND OPTIMIZATION:
Develop metrics and optimization approaches:
- Channel effectiveness measurement and tracking
- Audience engagement and response monitoring
- Message testing and refinement processes
- Resource allocation optimization based on performance
- Feedback loops and strategy adjustment mechanisms

Provide detailed, actionable communication strategy with specific channel selection and audience engagement tactics.
`
};
